import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import DataGrid, { DataGridCol } from "../Display/DataGrid";
import NumberInput from "../DataInput/NumberInput";
import {
  OU,
  OUTREACH_SANS,
  OuBone,
  OuCard,
  OuCardFooter,
  OuCardHeader,
  OuControl,
  OuEmptyState,
  OuField,
  OuNotice,
  OuPage,
  OuPill,
  OuPrimaryButton,
  OuSecondaryButton,
  OuSelect,
  OuSettingRow,
  OuSwitch,
  OuTone,
  QueueIcon,
  SyncIcon,
  useIsHubSpotCrm,
} from "../CrmUi";
import { getLinkedInAutomation, saveLinkedInAutomationSettings, syncLinkedInAutomationNow } from "../../services/firebase";
import { LinkedInAction, LinkedInAutomationSettings, LinkedInAutomationView } from "../../types/addons";
import PlanUpgradePrompt from "../Billing/PlanUpgradePrompt";

/* The three pacing numbers, side by side; one column on a phone. */
const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin: 20px 0 8px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Rows = styled("div")`
  margin-top: 16px;
`;

/* The select's column: a fixed width on desktop, the full row on a phone. */
const ControlSlot = styled("div")`
  width: 240px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 600px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const DaysField = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${OU.muted};
`;

/* The dashboard's grid, trimmed to Outreach's table: 12px cell gutters with
   the text lined up under the card title, 13px/600 muted headers on the card
   surface, Outreach's 64px rows split by 1px rules. */
const QueueGrid = styled("div")`
  margin: 12px -12px 0;

  && .MuiDataGrid-root {
    font-family: ${OUTREACH_SANS};
    color: ${OU.ink};
    --DataGrid-rowBorderColor: ${OU.rule};
  }

  && .MuiDataGrid-root .MuiDataGrid-columnHeader {
    padding: 0 12px !important;
    background-color: ${OU.surface} !important;
    border-bottom: 1px solid ${OU.rule} !important;
  }

  && .MuiDataGrid-root .MuiDataGrid-columnHeaderTitle {
    font: 600 13px/16px ${OUTREACH_SANS};
    color: ${OU.muted};
    white-space: nowrap;
  }

  && .MuiDataGrid-root .MuiDataGrid-columnSeparator {
    display: none;
  }

  && .MuiDataGrid-root .MuiDataGrid-row {
    min-height: 64px !important;
  }

  && .MuiDataGrid-root .MuiDataGrid-cell {
    min-height: 64px !important;
    padding: 0 12px !important;
    font: 400 14px/20px ${OUTREACH_SANS};
    color: ${OU.ink};
    border-bottom: 1px solid ${OU.rule} !important;
  }

  && .MuiDataGrid-root .MuiDataGrid-row:last-of-type .MuiDataGrid-cell {
    border-bottom: none !important;
  }

  && .MuiDataGrid-root .MuiDataGrid-footerContainer {
    background-color: ${OU.surface};
    border-top: 1px solid ${OU.rule} !important;
  }
`;

const STATUS_TONE: Record<LinkedInAction["status"], OuTone> = {
  completed: "success",
  failed: "danger",
  processing: "info",
  pending: "neutral",
  skipped: "neutral",
};

type QueueRow = LinkedInAction & { when: string; action: string; prospect: string; note: string };

const columns: DataGridCol<QueueRow>[] = [
  { field: "when", headerName: "When", flex: 1.1, minWidth: 170 },
  { field: "ownerEmail", headerName: "Who", flex: 1.2, minWidth: 160 },
  { field: "action", headerName: "Action", flex: 0.8, minWidth: 110 },
  { field: "prospect", headerName: "Prospect", flex: 1, minWidth: 130 },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 170,
    renderCell: (params) => (
      <OuPill tone={STATUS_TONE[params.row.status] || "neutral"}>
        {params.row.status}
        {params.row.outreach.taskCompleted ? ", task done" : ""}
      </OuPill>
    ),
  },
  { field: "note", headerName: "Note", flex: 1.5, minWidth: 120 },
];

const ACTION_LABELS: Record<LinkedInAction["actionType"], string> = {
  connect: "Connect",
  message: "Message",
  view_profile: "View profile",
  withdraw: "Withdraw",
  react: "React",
};

const STEP_SELECTION = [
  { value: "tagged", label: "Only steps tagged #auto" },
  { value: "all", label: "Every LinkedIn step" },
];

const WHEN_NOT_CONNECTED = [
  { value: "wait", label: "Leave the task open" },
  { value: "skip", label: "Complete the task and skip" },
  { value: "reschedule", label: "Push the task out" },
  { value: "withdraw", label: "Withdraw the request and skip" },
];

const SkeletonHeader: FC<{ titleWidth: number; lines?: number; aside?: React.ReactNode }> = ({ titleWidth, lines = 0, aside }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 24 }}>
    <div style={{ flex: "1 1 auto" }}>
      <OuBone variant="text" width={titleWidth} sx={{ fontSize: "1.25rem" }} />
      {lines > 0 && <OuBone variant="text" width="88%" />}
      {lines > 1 && <OuBone variant="text" width="54%" />}
    </div>
    {aside}
  </div>
);

const SkeletonRow: FC<{ control: React.ReactNode; helper?: boolean }> = ({ control, helper }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, padding: "16px 0", borderTop: `1px solid ${OU.rule}` }}>
    <div style={{ flex: "1 1 auto" }}>
      <OuBone variant="text" width={300} />
      {helper && <OuBone variant="text" width="80%" />}
    </div>
    {control}
  </div>
);

/**
 * The automation tab while its settings and queue load: the switch card,
 * the pacing card with its three number fields, its rules and its footer,
 * and the queue card.
 */
export const LinkedInAutomationTabSkeleton: FC = () => (
  <OuPage aria-busy="true">
    <OuCard>
      <SkeletonHeader titleWidth={200} lines={2} aside={<OuBone variant="rounded" width={48} height={24} sx={{ borderRadius: "12px" }} />} />
    </OuCard>

    <OuCard>
      <SkeletonHeader titleWidth={80} />
      <Grid>
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <OuBone variant="text" width={110} sx={{ fontSize: "13px", mb: "6px" }} />
            <OuBone variant="rounded" height={40} sx={{ borderRadius: "8px" }} />
          </div>
        ))}
      </Grid>
      <Rows>
        <SkeletonRow control={<OuBone variant="rounded" width={48} height={24} sx={{ borderRadius: "12px" }} />} />
        <SkeletonRow helper control={<OuBone variant="rounded" width={240} height={40} sx={{ borderRadius: "8px" }} />} />
      </Rows>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, margin: "8px -24px -24px", padding: "16px 24px", borderTop: `1px solid ${OU.rule}` }}>
        <OuBone variant="rounded" width={148} height={36} sx={{ borderRadius: "100px" }} />
        <OuBone variant="rounded" width={72} height={36} sx={{ borderRadius: "100px" }} />
      </div>
    </OuCard>

    <OuCard>
      <SkeletonHeader titleWidth={64} />
      <div style={{ marginTop: 16 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ display: "flex", gap: 16, padding: "14px 0", borderTop: i ? `1px solid ${OU.rule}` : "none" }}>
            <OuBone variant="text" width="18%" />
            <OuBone variant="text" width="24%" />
            <OuBone variant="text" width="14%" />
            <OuBone variant="text" width="18%" />
            <OuBone variant="rounded" width={84} height={24} sx={{ borderRadius: "100px" }} />
          </div>
        ))}
      </div>
    </OuCard>
  </OuPage>
);

/** Per-user LinkedIn automation for Outreach sequences and HubSpot LinkedIn tasks: the switch, pacing and hours, and the queue. Results live on Insights. */
const LinkedInAutomationTab: FC = () => {
  const [view, setView] = useState<LinkedInAutomationView | null>(null);
  const [settings, setSettings] = useState<LinkedInAutomationSettings | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // The automation works the org's CRM tasks: Outreach sequence steps or HubSpot LinkedIn tasks.
  const hubspot = useIsHubSpotCrm();
  const crm = hubspot ? "HubSpot" : "Outreach";

  const load = useCallback(async () => {
    const v = await getLinkedInAutomation(30);
    setView(v);
    setSettings(v.settings);
  }, []);
  useEffect(() => {
    load().catch(() => setMessage("Could not load automation."));
  }, [load]);

  const rows: QueueRow[] = useMemo(
    () =>
      (view?.queue || []).map((q) => ({
        ...q,
        when: new Date(q.scheduledAfter).toLocaleString(),
        action: ACTION_LABELS[q.actionType] || q.actionType,
        prospect: q.outreach.prospectName || q.outreach.prospectId || "",
        note: q.error || "",
      })),
    [view]
  );


  if ((!view || !settings) && message) {
    return (
      <OuPage>
        <OuCard>
          <OuCardHeader level="page" title="LinkedIn automation" />
          <OuNotice tone="danger">{message}</OuNotice>
        </OuCard>
      </OuPage>
    );
  }

  if (!view || !settings) return <LinkedInAutomationTabSkeleton />;

  const save = async (patch: Partial<LinkedInAutomationSettings>) => {
    setBusy("save");
    try {
      const next = await saveLinkedInAutomationSettings({ ...settings, ...patch });
      setSettings(next);
      setMessage(null);
    } catch (e: any) {
      setMessage(e?.message || "Could not save.");
    } finally {
      setBusy(null);
    }
  };

  const syncNow = async () => {
    setBusy("sync");
    try {
      const r = await syncLinkedInAutomationNow();
      setMessage(`Queued ${r.queued} task${r.queued === 1 ? "" : "s"}${r.skipped ? `, left ${r.skipped} manual` : ""}.`);
      await load();
    } catch (e: any) {
      setMessage(e?.message || "Sync failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <OuPage>
      <OuCard>
        <OuCardHeader
          level="page"
          title="LinkedIn automation"
          description={`Your open ${crm} LinkedIn tasks run through the extension while it is open, using each step's template. Results are on Insights.`}
          aside={
            <OuSwitch
              aria-label="LinkedIn automation"
              checked={settings.enabled}
              saving={busy === "save"}
              disabled={!view.eligible}
              onChange={(enabled) => save({ enabled })}
            />
          }
        />
        {view.planRequired ? (
          <PlanUpgradePrompt feature="linkedinAutomation" what="LinkedIn automation" />
        ) : !view.eligible && (
          <OuNotice tone="warning">
            {view.needsScopes ? `Reconnect ${crm} under Settings, Integration, so boring can read tasks and templates.` : `Connect ${crm} first.`}
          </OuNotice>
        )}
      </OuCard>

      <OuCard>
        <OuCardHeader title="Pacing" />
        <Grid>
          <OuField label="Actions per hour" htmlFor="li-actions-per-hour">
            <NumberInput id="li-actions-per-hour" value={settings.actionsPerHour} onChange={(v) => setSettings({ ...settings, actionsPerHour: v })} />
          </OuField>
          <OuField label="Active from (hour)" htmlFor="li-active-from">
            <NumberInput id="li-active-from" value={settings.activeHoursStart} onChange={(v) => setSettings({ ...settings, activeHoursStart: v })} />
          </OuField>
          <OuField label="Active until (hour)" htmlFor="li-active-until">
            <NumberInput id="li-active-until" value={settings.activeHoursEnd} onChange={(v) => setSettings({ ...settings, activeHoursEnd: v })} />
          </OuField>
        </Grid>

        <Rows>
          {hubspot ? (
            <OuSettingRow
              label="Run LinkedIn steps from HubSpot sequences automatically"
              htmlFor="li-hubspot-sequence-steps"
              helper="Connection request and InMail tasks run once they are due, with the task's notes as the note or the message. A message to someone who is not a connection yet is not sent; its task stays open with a note saying why. Off: only tasks with #auto in their title or notes run. A custom To-do step titled “Automated LinkedIn connect” or “Automated LinkedIn message” always runs, with its notes as the note or the message."
            >
              {/* Turning it off also clears the older "Every LinkedIn step" choice, which runs the same tasks. */}
              <OuSwitch
                id="li-hubspot-sequence-steps"
                checked={settings.hubspotSequenceSteps === true || settings.stepSelection === "all"}
                onChange={(v) =>
                  // One-offs used to follow this switch; pin them where they are so flipping it never changes them.
                  setSettings({ ...settings, hubspotSequenceSteps: v, stepSelection: v ? settings.stepSelection : "tagged", oneOffTasks: settings.oneOffTasks ?? (settings.hubspotSequenceSteps === true || settings.stepSelection === "all") })
                }
              />
            </OuSettingRow>
          ) : (
            <OuSettingRow
              label="Steps to automate"
              helper="Write #auto in a LinkedIn step's task note in Outreach to have boring run it. Steps without it stay manual tasks."
            >
              <ControlSlot>
                <OuControl style={{ width: "100%" }}>
                  <OuSelect
                    aria-label="Steps to automate"
                    value={settings.stepSelection || "tagged"}
                    onChange={(v) => setSettings({ ...settings, stepSelection: v as LinkedInAutomationSettings["stepSelection"] })}
                    options={STEP_SELECTION}
                  />
                </OuControl>
              </ControlSlot>
            </OuSettingRow>
          )}
          <OuSettingRow
            label="Run one-off LinkedIn tasks too"
            htmlFor="li-one-off-tasks"
            helper={`LinkedIn tasks created by hand in ${crm}, not by a sequence, run once they are due, with the task's note as the connection note or the message. A message task with no note stays manual. Only tasks due in the last 7 days run, so older open tasks are never sent. Off: only one-off tasks with #auto in their ${hubspot ? "title or notes" : "note"} run.`}
          >
            <OuSwitch
              id="li-one-off-tasks"
              checked={typeof settings.oneOffTasks === "boolean" ? settings.oneOffTasks : !!hubspot && (settings.hubspotSequenceSteps === true || settings.stepSelection === "all")}
              onChange={(v) => setSettings({ ...settings, oneOffTasks: v })}
            />
          </OuSettingRow>
          <OuSettingRow label={`Complete the ${crm} task after each action`} htmlFor="li-complete-tasks">
            <OuSwitch id="li-complete-tasks" checked={settings.completeTasks} onChange={(v) => setSettings({ ...settings, completeTasks: v })} />
          </OuSettingRow>
          <OuSettingRow
            label="When a message is due but the request isn't accepted"
            helper={`LinkedIn only delivers messages to connections, so the message never goes before they accept. Leave the ${crm} task open to keep waiting, complete it and move on, push it out, or withdraw the invitation and move on.`}
          >
            <ControlSlot>
              <OuControl style={{ width: "100%" }}>
                <OuSelect
                  aria-label="When a message is due but the request isn't accepted"
                  value={settings.whenNotConnected}
                  onChange={(v) => setSettings({ ...settings, whenNotConnected: v as LinkedInAutomationSettings["whenNotConnected"] })}
                  options={WHEN_NOT_CONNECTED}
                />
              </OuControl>
            </ControlSlot>
          </OuSettingRow>
          {settings.whenNotConnected === "reschedule" && (
            <OuSettingRow label="Push the task out by" htmlFor="li-reschedule-days">
              <ControlSlot>
                <DaysField>
                  <OuControl style={{ width: 96 }}>
                    <NumberInput id="li-reschedule-days" value={settings.rescheduleDays} onChange={(v) => setSettings({ ...settings, rescheduleDays: v })} />
                  </OuControl>
                  <span>Days</span>
                </DaysField>
              </ControlSlot>
            </OuSettingRow>
          )}
        </Rows>

        <OuCardFooter status={message}>
          <OuSecondaryButton type="button" icon={<SyncIcon />} disabled={busy !== null || !view.eligible} loading={busy === "sync"} onClick={syncNow}>
            Sync tasks now
          </OuSecondaryButton>
          <OuPrimaryButton type="button" disabled={busy !== null} loading={busy === "save"} onClick={() => save({})}>
            Save
          </OuPrimaryButton>
        </OuCardFooter>
      </OuCard>

      <OuCard>
        <OuCardHeader title="Queue" />
        {rows.length === 0 ? (
          <OuEmptyState icon={<QueueIcon />} title="Nothing queued yet" hint="Turn automation on and sync tasks." />
        ) : (
          <QueueGrid>
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight
              rowHeight={64}
              getRowHeight={() => 64}
              columnHeaderHeight={44}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter={rows.length <= 25}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[25, 50]}
            />
          </QueueGrid>
        )}
      </OuCard>
    </OuPage>
  );
};

export default LinkedInAutomationTab;
